import { Controller } from "@hotwired/stimulus"

// Drives the new-upload form client-side: a Google-style customer typeahead
// (type to filter, results show below, arrow/Enter or click to pick), a customer-dependent
// template <select>, and a drag-and-drop file zone with a name/size preview and a
// clear button. A lone customer is preselected on load, and a chosen customer
// with a single template auto-selects that template — so the common
// one-customer/one-template case lands ready to just drop a file. Required-field
// messages come from data-required-message so they stay localized.
export default class extends Controller {
  static targets = [
    "customerInput", "customerList", "customerOption", "template",
    "fileInput", "fileName", "fileSize", "empty", "preview", "dropZone",
    "actions", "feedback",
  ]
  static values = { checkUrl: String }

  connect() {
    this.selectedCustomerId = null
    this.activeIndex = 0
    this.#syncTemplateState()
    this.#preselectSingleCustomer()
    // Close the results list on a click outside the field/list (not on blur,
    // which races synthetic typing); picking or Escape also closes it.
    this.closeOnOutsideClick = (event) => {
      if (!this.customerInputTarget.contains(event.target) && !this.customerListTarget.contains(event.target)) {
        this.customerListTarget.hidden = true
      }
    }
    document.addEventListener("click", this.closeOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutsideClick)
  }

  // Google-style typeahead: as the user types, show the matching customers below
  // the input; clicking (or Enter on the highlighted one) picks it. Any typed
  // value that hasn't been picked clears the selection and flags the field.
  filterCustomers() {
    const query = this.customerInputTarget.value.trim().toLowerCase()
    let matches = 0
    let exact = null
    this.customerOptionTargets.forEach((option) => {
      const name = (option.dataset.name || "").toLowerCase()
      const hit = query.length > 0 && name.includes(query)
      option.style.display = hit ? "" : "none"
      if (hit) matches += 1
      if (name === query) exact = option
    })
    this.customerListTarget.hidden = !(query.length > 0 && matches > 0)
    this.#setActive(0)

    // Keep the selection when the field already holds a full customer name (a
    // single-customer preselect, or re-focusing a chosen field); a partial query
    // is not yet a pick. Without this, autofocus firing `focus->filterCustomers`
    // would wipe the preselected customer and re-disable the template select.
    if (exact) {
      this.selectedCustomerId = exact.dataset.id || null
      this.customerInputTarget.setCustomValidity("")
    } else {
      this.selectedCustomerId = null
      this.#syncCustomerValidity()
    }
    this.#filterTemplates()
    this.#syncTemplateState()
  }

  navigate(event) {
    if (this.customerListTarget.hidden) return
    const visible = this.#visibleOptions()
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.#setActive(this.activeIndex + 1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.#setActive(this.activeIndex - 1)
    } else if (event.key === "Enter" && visible.length > 0) {
      event.preventDefault()
      this.#choose(visible[this.activeIndex] || visible[0])
    } else if (event.key === "Escape") {
      this.customerListTarget.hidden = true
    }
  }

  selectCustomer(event) {
    event.preventDefault()
    this.#choose(event.currentTarget)
  }

  templateChanged() {
    this.templateTarget.setCustomValidity("")
  }

  // The check step: dry-run the file server-side (no save) and swap in the
  // returned feedback fragment. The file never leaves the still-attached input,
  // so the form's real submit (send) can use it afterwards.
  async check() {
    if (!this.element.checkValidity()) {
      this.element.reportValidity()
      return
    }
    // A raw fetch (unlike a Turbo submit) must carry the CSRF token itself.
    const headers = { Accept: "text/html" }
    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (token) headers["X-CSRF-Token"] = token

    let response
    try {
      response = await fetch(this.checkUrlValue, { method: "POST", body: new FormData(this.element), headers })
    } catch {
      return // network error: leave the form as-is so the user can retry
    }
    // Only swap in a genuine check fragment (marked by the controller). A
    // framework error page — incl. a CSRF 422 — lacks the header, so it is never
    // injected and the Check/Cancel actions stay available for a retry.
    if (!response.headers.get("X-Upload-Check")) return

    this.feedbackTarget.innerHTML = await response.text()
    if (this.hasActionsTarget) this.actionsTarget.hidden = true
  }

  cancelCheck() {
    this.feedbackTarget.innerHTML = ""
    if (this.hasActionsTarget) this.actionsTarget.hidden = false
  }

  openFileDialog(event) {
    if (event?.target.closest("button")) return
    this.fileInputTarget.click()
  }

  fileChanged() {
    this.#updatePreview()
  }

  clearFile(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.fileInputTarget.value = ""
    this.#updatePreview()
  }

  dragover(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-orange-400")
  }

  dragleave() {
    this.dropZoneTarget.classList.remove("border-orange-400")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-orange-400")
    const files = event.dataTransfer.files
    if (files.length > 0) {
      const dt = new DataTransfer()
      dt.items.add(files[0])
      this.fileInputTarget.files = dt.files
      this.#updatePreview()
    }
  }

  // Swap the browser's English default ("Please fill out this field") for the
  // localized data-required-message, leaving any more specific custom error
  // (the not-in-list message) untouched, and clear it once the field is valid.
  markInvalid(event) {
    const el = event.currentTarget
    if (el.validity.valid) {
      el.setCustomValidity("")
      return
    }
    if (el.validity.customError) return
    el.setCustomValidity(el.dataset.requiredMessage || "")
  }

  #preselectSingleCustomer() {
    if (this.customerOptionTargets.length === 1) this.#choose(this.customerOptionTargets[0])
  }

  #choose(option) {
    this.customerInputTarget.value = option.dataset.name || ""
    this.selectedCustomerId = option.dataset.id || null
    this.customerInputTarget.setCustomValidity("")
    this.customerListTarget.hidden = true
    this.#filterTemplates()
    this.#syncTemplateState()
  }

  #syncCustomerValidity() {
    const typed = this.customerInputTarget.value.trim()
    this.customerInputTarget.setCustomValidity(
      typed ? this.customerInputTarget.dataset.notInListMessage || "" : ""
    )
  }

  #visibleOptions() {
    return this.customerOptionTargets.filter((option) => option.style.display !== "none")
  }

  #setActive(index) {
    const visible = this.#visibleOptions()
    if (visible.length === 0) {
      this.activeIndex = 0
      return
    }
    this.activeIndex = (index + visible.length) % visible.length
    visible.forEach((option, i) => option.classList.toggle("bg-zinc-100", i === this.activeIndex))
    visible.forEach((option, i) => option.classList.toggle("dark:bg-zinc-800", i === this.activeIndex))
    visible[this.activeIndex]?.scrollIntoView({ block: "nearest" })
  }

  #filterTemplates() {
    const visible = []
    Array.from(this.templateTarget.options).forEach((option) => {
      if (!option.value) {
        option.hidden = false
        return
      }
      const show = (option.dataset.customerIds || "").split(" ").includes(this.selectedCustomerId)
      option.hidden = !show
      if (show) visible.push(option)
    })
    // Auto-select when the chosen customer has exactly one template.
    this.templateTarget.value = visible.length === 1 ? visible[0].value : ""
    this.templateTarget.setCustomValidity("")
  }

  #syncTemplateState() {
    this.templateTarget.disabled = !this.selectedCustomerId
  }

  #updatePreview() {
    const file = this.fileInputTarget.files[0]
    if (file) {
      this.fileNameTarget.textContent = file.name
      if (this.hasFileSizeTarget) this.fileSizeTarget.textContent = `${(file.size / 1024).toFixed(1)} KB`
      this.fileInputTarget.setCustomValidity("")
      this.emptyTarget.classList.add("hidden")
      this.previewTarget.classList.remove("hidden")
      this.previewTarget.classList.add("flex")
    } else {
      this.fileNameTarget.textContent = ""
      if (this.hasFileSizeTarget) this.fileSizeTarget.textContent = ""
      this.previewTarget.classList.add("hidden")
      this.previewTarget.classList.remove("flex")
      this.emptyTarget.classList.remove("hidden")
    }
  }
}
